/**
 * Umbraco preview state for the SPA.
 *
 * The backoffice (umbraco.tracker-gps.com) and the API (api.wehunt.app) live on
 * different hosts, so the preview cookies Umbraco sets when an editor clicks
 * Preview never reach the API: without proof of an editor session the API can
 * only ever answer with PUBLISHED content, and preview shows stale pages.
 *
 * The preview redirect therefore hands the backoffice ticket over once, in the
 * `c` query parameter (/{id}?preview=true&lang=xx&c={ticket}). We keep it for
 * the lifetime of the tab and forward it on every API call made while the page
 * is in preview mode (`?preview` in the URL), as the X-Preview-Token header.
 * The backend validates the ticket and switches to draft content; the header
 * also tells the backend's output cache to bypass itself for the request.
 */

export const PREVIEW_TOKEN_HEADER = "X-Preview-Token";

const STORAGE_KEY = "wehunt-preview-token";

/** `undefined` = not read from sessionStorage yet; `null` = read, nothing stored. */
let cachedToken: string | null | undefined;

/**
 * True when the current page was loaded with `?preview`, i.e. the editor came
 * here through the Umbraco Preview button.
 */
export function isPreviewMode(): boolean {
  try {
    return new URLSearchParams(window.location.search).has("preview");
  } catch {
    return false;
  }
}

/** Remembers the backoffice ticket the preview redirect handed over. */
export function storePreviewToken(token: string): void {
  cachedToken = token;
  try {
    sessionStorage.setItem(STORAGE_KEY, token);
  } catch {
    // sessionStorage unavailable — the in-memory copy still serves this page load.
  }
}

/** The stored backoffice ticket, or null when none was handed over in this tab. */
export function getPreviewToken(): string | null {
  if (cachedToken !== undefined) return cachedToken;

  try {
    cachedToken = sessionStorage.getItem(STORAGE_KEY);
  } catch {
    cachedToken = null;
  }
  return cachedToken;
}

/**
 * The header(s) an API call must carry so the backend serves draft content.
 * Empty outside preview mode or when no ticket is available, so a stale ticket
 * can never leak into ordinary browsing.
 */
export function previewHeaders(): Record<string, string> {
  if (!isPreviewMode()) return {};

  const token = getPreviewToken();
  return token ? { [PREVIEW_TOKEN_HEADER]: token } : {};
}

/** Test hook: forget the in-memory copy so the next read hits sessionStorage. */
export function resetPreviewTokenCache(): void {
  cachedToken = undefined;
}
